import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import greenCheck from "../../../assests/tenant/images/green-check.svg";
import { hasPermission } from "../../../utils/hasPermission";
import {
  useClearKycOfUserMutation,
  useSearchUserKycMutation,
  useUpdateKycOfUserMutation,
} from "../../../features/tenant/KycUpdate/KycUpdateApi";
import { useGetUserActivityMutation } from "../../../features/tenant/userActivity/userActivityApi";
import { UpdatedByCell } from "../UserList/UserActivityModal";

const KYC_LOG_FIELDS = ["aadhar", "pan", "gstin"];

const KYC_FORM_FIELDS = [
  { label: "Aadhar", name: "aadhar", placeholder: "Enter Aadhar", maxLength: 12 },
  { label: "PAN", name: "pan", placeholder: "Enter PAN", maxLength: 10 },
  { label: "GSTIN", name: "gstin", placeholder: "Enter GSTIN", maxLength: 15 },
];

const isEmptyActivityValue = (value) => {
  if (value === null || value === undefined || value === "") return true;
  if (typeof value === "string" && !value.trim()) return true;
  if (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  ) {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

const normalizeActivityValue = (value) => {
  if (isEmptyActivityValue(value)) return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return normalizeActivityValue(JSON.parse(trimmed));
      } catch (error) {
        return trimmed;
      }
    }

    return trimmed;
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeActivityValue(item));
  }

  if (typeof value === "object" && value !== null) {
    const normalized = {};
    Object.keys(value)
      .sort()
      .forEach((key) => {
        normalized[key] = normalizeActivityValue(value[key]);
      });
    return normalized;
  }

  return value;
};

const areActivityValuesEqual = (a, b) => {
  if (a === b) return true;

  try {
    return (
      JSON.stringify(normalizeActivityValue(a)) ===
      JSON.stringify(normalizeActivityValue(b))
    );
  } catch (error) {
    return false;
  }
};

const KycUpdate = () => {
  const tenantId = JSON.parse(sessionStorage.getItem("vendorData")).tenant_id;
  const token = sessionStorage.getItem("access_token");
  const canUpdate = hasPermission("kycUpdate");

  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [userData, setUserData] = useState(null);
  const [kycLogs, setKycLogs] = useState([]);
  const [kycFormData, setKycFormData] = useState({
    aadhar: "",
    is_valid_aadhar: false,
    edited_aadhar: false,
    pan: "",
    is_valid_pan: false,
    edited_pan: false,
    gstin: "",
    is_valid_gstin: false,
    edited_gstin: false,
  });

  const [searchUserKyc, { isLoading: searchLoading }] = useSearchUserKycMutation();
  const [updateKycOfUser, { data: updateKycData, error: updateKycError }] =
    useUpdateKycOfUserMutation();
  const [clearKycUser, { data: clearKycUserData, error: clearKycUserError }] =
    useClearKycOfUserMutation();
  const [getUserActivity] = useGetUserActivityMutation();

  const syncKycForm = (kyc = {}) => {
    setKycFormData({
      aadhar: kyc?.aadhar || "",
      pan: kyc?.pan || "",
      gstin: kyc?.gstin || "",
      is_valid_aadhar: !!kyc?.is_valid_aadhar,
      is_valid_pan: !!kyc?.is_valid_pan,
      is_valid_gstin: !!kyc?.is_valid_gstin,
      edited_aadhar: false,
      edited_pan: false,
      edited_gstin: false,
    });
  };

  const fetchKycLogs = async (appUserId) => {
    const response = await getUserActivity({
      token,
      tenant_id: tenantId,
      filters: { app_user_id: appUserId, limit: 50, page: 1 },
    });

    const logs = (response?.data?.body?.list || [])
      .map((log) => {
        const keys = Object.keys(log?.previous_values || {}).filter(
          (key) =>
            KYC_LOG_FIELDS.includes(key) &&
            !areActivityValuesEqual(
              log.previous_values[key],
              log.new_values?.[key]
            )
        );
        if (!keys.length) return null;

        return keys.map((key) => ({
          field: key,
          previous: log.previous_values[key],
          updated: log.new_values?.[key],
          updated_by: log.updated_by,
          panel_admin_name: log.panel_admin_name,
          panel_admin_role: log.panel_admin_role,
          panel_admin_designation: log.panel_admin_designation,
          created_at: log.created_at,
        }));
      })
      .filter(Boolean)
      .flat();

    setKycLogs(logs);
  };

  const loadUser = async (searchFilters) => {
    const response = await searchUserKyc({
      token,
      tenant_id: tenantId,
      filters: searchFilters,
    }).unwrap();

    if (!response?.body?.user) {
      setUserData(null);
      setKycLogs([]);
      syncKycForm({});
      toast.error("User not found");
      return;
    }

    setUserData(response.body);
    syncKycForm(response.body.user?.kyc || {});
    await fetchKycLogs(response.body.user.id);
  };

  const handleSearch = async () => {
    if (!mobile.trim() && !email.trim()) {
      toast.error("Enter mobile or email to search");
      return;
    }

    try {
      await loadUser({
        mobile: mobile.trim(),
        email: email.trim(),
      });
    } catch (error) {
      toast.error("Failed to fetch user");
    }
  };

  const handleClear = () => {
    setMobile("");
    setEmail("");
    setUserData(null);
    setKycLogs([]);
    syncKycForm({});
  };

  const refreshUser = async () => {
    if (!userData?.user?.id) return;
    await loadUser({ app_user_id: userData.user.id });
  };

  const handleCheckboxChange = (field) => {
    setKycFormData((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleFormValueChange = (type, value) => {
    setKycFormData((prev) => ({
      ...prev,
      [type]: value,
      [`edited_${type}`]: true,
    }));
  };

  const handleUpdateKycOnSubmit = (e) => {
    e.preventDefault();
    if (!canUpdate) return;

    const validFields = {};
    if (kycFormData.is_valid_aadhar && kycFormData.aadhar && kycFormData.edited_aadhar) {
      validFields.aadhar = kycFormData.aadhar;
      validFields.is_aadhar = true;
    }
    if (kycFormData.is_valid_pan && kycFormData.pan && kycFormData.edited_pan) {
      validFields.pan = kycFormData.pan;
      validFields.is_pan = true;
    }
    if (kycFormData.is_valid_gstin && kycFormData.gstin && kycFormData.edited_gstin) {
      validFields.gstin = kycFormData.gstin;
      validFields.is_gstin = true;
    }

    if (!Object.keys(validFields).length) {
      toast.error("Select and edit at least one field to update");
      return;
    }

    if (!window.confirm("Are you sure you want to update KYC?")) return;

    updateKycOfUser({
      data: validFields,
      token,
      tenant_id: tenantId,
      app_user_id: userData.user.id,
    });
  };

  const handleClearKycOfUser = (type) => {
    if (!window.confirm(`Clear ${type.toUpperCase()} for this user?`)) return;

    clearKycUser({
      data: { type },
      token,
      tenant_id: tenantId,
      app_user_id: userData.user.id,
    });
  };

  useEffect(() => {
    if (!updateKycData?.body) return;

    const success = Object.entries(updateKycData.body)
      .filter(([, value]) => value === "success")
      .map(([key]) => key.replace("_status", "").toUpperCase());

    if (success.length) {
      toast.success(`Updated: ${success.join(", ")}`);
      refreshUser();
    }
  }, [updateKycData]);

  useEffect(() => {
    if (updateKycError) toast.error("Failed to update KYC");
  }, [updateKycError]);

  useEffect(() => {
    if (clearKycUserData) {
      toast.success("KYC cleared");
      refreshUser();
    }
  }, [clearKycUserData]);

  useEffect(() => {
    if (clearKycUserError) toast.error("Failed to clear KYC");
  }, [clearKycUserError]);

  const latestDigilocker = userData?.digilocker?.[0];

  return (
    <div className="flex w-full bg-gray-200 h-full justify-center">
      <div className="w-full">
        <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
          KYC Update
          <LinksComponent />
        </div>

        <div className="p-3">
          <div className="bg-white shadow rounded-lg">
            <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3">
              <div className="flex opt_w100 space-x-2 items-end">
                <div className="w-52 searchin">
                  <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                    Mobile No
                  </label>
                  <input
                    type="text"
                    className="w-full datepd text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                    placeholder="Enter Mobile No"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                  />
                </div>

                <div className="w-52 searchin">
                  <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                    Email
                  </label>
                  <input
                    type="text"
                    className="w-full datepd text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                    placeholder="Enter Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary clear-both float-right pdbtn_all"
                    onClick={handleSearch}
                    disabled={searchLoading}
                  >
                    {searchLoading ? "Searching..." : "Search"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary clear-both float-right pdbtn_all"
                    onClick={handleClear}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>

            {userData?.user && (
              <div className="p-4">
                <div className="mb-4 pb-3 border-b border-gray-200 text-sm">
                  <span className="font-semibold">{userData.user.name}</span>
                  <span className="text-gray-400 mx-2">|</span>
                  <span>{userData.user.mobile}</span>
                  <span className="text-gray-400 mx-2">|</span>
                  <span className="capitalize">{userData.user.user_type}</span>
                  {latestDigilocker && (
                    <>
                      <span className="text-gray-400 mx-2">|</span>
                      <span className="text-gray-600">
                        Digilocker: {latestDigilocker.status_label}
                      </span>
                    </>
                  )}
                </div>

                {canUpdate && (
                  <form onSubmit={handleUpdateKycOnSubmit}>
                    <div className="grid grid-cols-1 gap-4 max-w-3xl">
                      {KYC_FORM_FIELDS.map((field) => (
                        <div key={field.name} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={kycFormData[`is_valid_${field.name}`]}
                            onChange={() =>
                              handleCheckboxChange(`is_valid_${field.name}`)
                            }
                            className="w-5 h-5"
                          />
                          <div className="flex-1">
                            <label className="text-xs font-semibold text-gray-600 uppercase">
                              {field.label}
                              {kycFormData[`is_valid_${field.name}`] && (
                                <img
                                  src={greenCheck}
                                  alt="verified"
                                  className="w-4 h-4 inline ml-1"
                                />
                              )}
                            </label>
                            <input
                              className="mt-1 w-full border border-gray-300 rounded-lg p-2 text-sm bg-gray-50"
                              placeholder={field.placeholder}
                              value={kycFormData[field.name] || ""}
                              onChange={(e) =>
                                handleFormValueChange(
                                  field.name,
                                  e.target.value.replace(/[^a-zA-Z0-9]/g, "")
                                )
                              }
                              maxLength={field.maxLength}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleClearKycOfUser(field.name)}
                            className={`px-3 py-2 rounded text-xs mt-5 ${
                              kycFormData[`is_valid_${field.name}`]
                                ? "bg-red-600 text-white"
                                : "bg-gray-300 text-gray-500 cursor-not-allowed"
                            }`}
                            disabled={!kycFormData[`is_valid_${field.name}`]}
                          >
                            Clear
                          </button>
                        </div>
                      ))}
                    </div>

                    <button
                      type="submit"
                      className="mt-4 text-white bg-blue-700 hover:bg-blue-800 font-medium rounded-lg text-sm px-4 py-2"
                    >
                      Update
                    </button>
                  </form>
                )}

                <div className="mt-6">
                  <h3 className="font-semibold text-sm mb-2">KYC Logs</h3>
                  {kycLogs.length === 0 ? (
                    <p className="text-sm text-gray-500">No logs found.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-b text-sm">
                        <thead>
                          <tr>
                            <th className="bg-blue-700 text-white">Field</th>
                            <th className="bg-blue-700 text-white">Previous</th>
                            <th className="bg-blue-700 text-white">Updated</th>
                            <th className="bg-blue-700 text-white">Source</th>
                            <th className="bg-blue-700 text-white">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kycLogs.map((log, index) => (
                            <tr key={index}>
                              <td>{log.field}</td>
                              <td>{String(log.previous ?? "-")}</td>
                              <td>{String(log.updated ?? "-")}</td>
                              <td>
                                <UpdatedByCell item={log} />
                              </td>
                              <td>{new Date(log.created_at).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KycUpdate;
